<template>
  <div class="site-search-box w-full lg:relative">
    <form
      ref="searchForm"
      class="search-form-box relative grid p-0 gap-2 grid-cols-[1fr_auto] lg:inline-block lg:w-full"
      :class="{b2bMasthead__search: b2b}"
      :qaid="qaids.form"
      :name="names.form"
      role="search"
      method="GET"
      :action="`${encodedContextPath}/search/`"
      @submit="onSubmit"
      @reset="clear"
    >
      <label id="search-label" class="sr-only" :for="`label-${componentUid}`">
        {{ $t('text.search') }}
      </label>
      <input
        :id="`label-${componentUid}`"
        ref="searchInput"
        v-model="searchTerm"
        :qaid="qaids.input"
        class="js-site-search-input siteSearchInput primary-navigation__input text-small search-form-box__input multibrand-page-search text-md p-1 my-0 ml-0 mr-1 lg:m-0 w-full max-w-full font-normal"
        type="text"
        name="text"
        aria-autocomplete="list"
        :aria-controls="qaids.dropdown"
        aria-haspopup="listbox"
        :aria-expanded="isDropdownVisible"
        :aria-activedescendant="selectedId"
        role="combobox"
        spellcheck="false"
        autocomplete="off"
        maxlength="100"
        :placeholder="$t('search.placeholder')"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
        @keydown="handleKeydown"
      />
      <button
        qaid="search-btn-submit"
        type="submit"
        class="search-form-box__btn search-form-box__btn-search absolute right-10 top-1 lg:left-2 lg:top-[.4rem] lg:right-auto"
        :class="{active: isSearchBoxFocused}"
      >
        <pdl-icon name="search" :label="$t('text.search')" />
      </button>
      <button
        v-show="isSearchCrossVisible"
        qaid="search-btn-close"
        type="reset"
        :class="{active: isSearchBoxFocused && viewBreakpointMoreThan('large')}"
        class="search-form-box__btn search-form-box__btn-close lg:absolute lg:right-2 lg:top-[.375rem]"
      >
        <pdl-icon name="close" :label="$t('popup.close')" />
      </button>
    </form>
    <search-autocomplete-dropdown
      v-show="isDropdownVisible"
      :id="qaids.dropdown"
      ref="dropdown"
      :display-images="displayImages"
      :result="queryResult"
      :class="{active: isDropdownVisible}"
      :selected-id="selectedId"
      @onSelected="onSelected"
    />
  </div>
</template>

<script>
import {mapState, mapGetters} from 'vuex';
import debounce from 'lodash/debounce';
import storefrontInstance from '@/api/instances/storefront';
import SearchAutocompleteDropdown from './SearchAutocompleteDropdown';
import {PdlIcon} from '@pedal/pdl-icon';

export default {
  name: 'SearchInput',
  components: {SearchAutocompleteDropdown, PdlIcon},

  props: {
    componentUid: {
      type: String,
      required: true,
    },
    minCharacter: {
      type: String,
      default: '2',
    },
    waitTime: {
      type: String,
      default: '100',
    },
    displayImages: {
      type: String,
      default: 'true',
    },
    focused: {
      type: Function,
      default: () => null,
    },
  },

  data() {
    return {
      searchTerm: '',
      categories: [],
      products: [],
      suggestions: [],
      isSearchBoxFocused: false,
      isDropdownVisible: false,
      debounce: null,
      searchContainer: null,
      searchBlurTimeout: undefined,
      selectedId: null,
    };
  },

  computed: {
    autocompleteUrl() {
      return `search/autocomplete/${this.componentUid}`;
    },

    names() {
      return {
        form: `search_form_${this.componentUid}`,
      };
    },

    qaids() {
      return {
        form: `search-form-${this.componentUid}`,
        input: `input-${this.componentUid}`,
        dropdown: `search-dropdown-${this.componentUid}`,
      };
    },

    queryResult() {
      return {
        categories: this.categories ?? [],
        products: this.products ?? [],
        suggestions: this.suggestions ?? [],
      };
    },
    hasSearchData() {
      return this.categories?.length > 0 || this.products?.length > 0 || this.suggestions?.length > 0;
    },
    isSearchCrossVisible() {
      return this.searchTerm.trim().length > 0 || this.viewBreakpointLessThan('large');
    },
    isSearchTermLonger() {
      return this.searchTerm.length > parseInt(this.minCharacter) - 1;
    },

    shouldDropdownOpen() {
      return this.hasSearchData && this.isSearchTermLonger;
    },

    ...mapState('backend', ['encodedContextPath', 'b2b']),
    ...mapGetters('viewSize', ['viewBreakpointLessThan', 'viewBreakpointMoreThan']),
  },

  watch: {
    isSearchBoxFocused(newVal) {
      this.$emit('focused', newVal);
    },
  },

  mounted() {
    this.searchContainer = this.$refs.searchForm.closest('.nav__search-container');
  },

  methods: {
    /**
     * Doing the original work of jQuery Plugin, when selecting any suggestion from dropdown, placing the
     * suggestion text in input element and focusing it.
     */
    onSelected(value) {
      this.searchTerm = value;
      this.$refs.searchInput.focus();
    },
    onSubmit() {
      this.searchTerm = this.searchTerm.trim();
    },
    clear() {
      this.searchTerm = '';
      this.closeDropdown();
    },
    onBlur() {
      this.isSearchBoxFocused = false;

      setTimeout(() => {
        document.removeEventListener('click', this.closeOnClickOutside);
        this.searchContainer.classList.remove('nav__search-container--active');
      }, 200);
    },
    onFocus() {
      if (this.isSearchBoxFocused) return;
      this.isSearchBoxFocused = true;
      this.searchContainer.classList.add('nav__search-container--active');

      setTimeout(() => {
        document.addEventListener('click', this.closeOnClickOutside); // No `e` needed here
      }, 50);
    },
    onInput() {
      if (this.isSearchTermLonger) {
        if (!this.debounce) {
          this.debounce = debounce(this.fireSearchRequest, parseInt(this.waitTime));
        }
        return this.debounce();
      } else {
        // Close the drop down if already opened by previous query.
        this.closeDropdown();
      }
    },
    closeDropdown() {
      this.isDropdownVisible = false;
    },
    openDropdown() {
      this.isDropdownVisible = true;
    },

    async fireSearchRequest() {
      try {
        let response = await storefrontInstance.get(this.autocompleteUrl, {params: {text: this.searchTerm.trim()}});
        if (!response) return;
        this.categories = response.data?.categories;
        this.products = response.data?.products;
        this.suggestions = response.data?.suggestions;

        //If results comes late, for example while backspacing, when the last character is left, it'll still fire the query and after getting the result will open the drop-down.
        if (this.shouldDropdownOpen) {
          this.openDropdown();
        } else {
          this.closeDropdown();
        }
      } catch (error) {
        this.closeDropdown();
        console.error(error);
      }
    },
    closeOnClickOutside(event) {
      const el = this.$refs.searchForm;
      if (event.target === el || (el.contains(event.target) && this.shouldDropdownOpen)) {
        this.openDropdown();
      } else {
        this.closeDropdown();
      }
    },
    handleKeydown(event) {
      if (!this.isDropdownVisible) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.handleNavigationDown();
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.handleNavigationUp();
          break;
        case 'Enter':
          if (!this.selectedId) return;
          event.preventDefault();
          this.$refs.dropdown.triggerSelected();
          break;
        case 'Escape':
          event.preventDefault();
          this.closeDropdown();
          break;
      }
    },
    handleNavigationDown() {
      const dropdown = this.$refs.dropdown;
      if (!dropdown?.allData?.length) return;

      this.selectedId = !this.selectedId ? dropdown?.allData[0]?.id : dropdown.getNextId() || this.selectedId;
    },
    handleNavigationUp() {
      const previousId = this.$refs.dropdown.getPreviousId();
      if (previousId) this.selectedId = previousId;
    },
  },
};
</script>

<style lang="scss">
.site-search-box {
  .search-form-box {
    &__input {
      height: calc(2.25rem + 2px);
    }

    &__btn-search {
      color: var(--nav-search-icon-color);

      &.active {
        color: var(--nav-search-icon-color-focus);
      }
    }

    &__btn-close {
      color: var(--nav-search-icon-color);

      @media (screen(lg)) {
        color: var(--nav-search-placeholder-color-focus);
      }
    }

    .siteSearchInput {
      color: var(--nav-search-color);
      border: 1px solid var(--nav-search-border);
      background-color: var(--nav-search-bg);
      transition: all 0.1s ease;

      @media (screen(lg)) {
        padding-left: 36px;
      }

      &::placeholder {
        color: var(--nav-search-placeholder-color);
        opacity: 1;
      }

      &:focus,
      &:active {
        --nav-search-border: var(--nav-search-border-focus);
        --nav-search-bg: var(--nav-search-bg-focus);
        --nav-search-color: var(--nav-search-color-focus);
        --nav-search-placeholder-color: var(--nav-search-placeholder-color-focus);

        translate: 0 0;
        outline: none;

        ~ .search__btn-search {
          color: var(--nav-search-color-focus);
        }

        ~ .search__btn-close {
          color: var(--nav-search-icon-color-focus);
        }
      }

      ~ .search__btn-search {
        color: var(--nav-search-color);
      }

      ~ .search__btn-close {
        color: var(--nav-search-placeholder-color);
      }
    }
  }
}
</style>
